import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { lighten } from '@mui/material/styles';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axios from 'axios';
import { ReportContext, ThemeContext, UserContext } from '../Context';
import { getIndiceColor } from '../tool/GetIndiceInfos';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';
import { normalizeReportNumber, normalizeReportYear, splitReportInput } from '../../utils/reportNumber';
import {
  AuthorizedPeople,
  ChoiceCard,
  VisibilityChoice,
  getAccessPalette,
  getAutocompleteSlotProps,
  getInputSx,
  getPrimaryButtonSx,
  getRestrictionMode,
  getSectionTitleSx
} from '../tool/AccessRights';

const SUBSEQUENT_INDICE = '__';
const FIELD_ORDER = ['indice', 'number', 'year', 'unit', 'access'];

function AddReport({ prefilledNumber = '', prefilledYear = '' }) {
  const { user } = useContext(UserContext);
  const { pickReport } = useContext(ReportContext);
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';
  const c = getAccessPalette(isDark, 'app');
  const me = typeof user === 'object' ? user?.matricule : user;
  const currentYear = new Date().getFullYear();

  const [reportType, setReportType] = useState('initial');
  const [indiceValue, setIndiceValue] = useState('');
  const [numberValue, setNumberValue] = useState(String(prefilledNumber || ''));
  const [yearValue, setYearValue] = useState(String(prefilledYear || currentYear));
  const [unitValue, setUnitValue] = useState('');
  const [accessType, setAccessType] = useState('public');
  const [accessUsers, setAccessUsers] = useState(me ? [me] : []);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [creating, setCreating] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const indiceInputRef = useRef(null);
  const numberInputRef = useRef(null);
  const yearInputRef = useRef(null);
  const unitInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const token = sessionStorage.getItem('authToken');

    Promise.allSettled([
      axios.get('/api/user', { headers: { Authorization: `Bearer ${token}` } }),
      axios.get('/api/settings')
    ]).then(([usersResult, settingsResult]) => {
      if (cancelled) return;
      if (usersResult.status === 'fulfilled' && Array.isArray(usersResult.value.data)) {
        const list = usersResult.value.data;
        setUsers(list);
        const myService = list.find((person) => person.matricule === me)?.service;
        if (myService) setUnitValue((value) => value || myService);
      } else {
        setLoadError('Impossible de charger la liste des utilisateurs.');
      }
      if (settingsResult.status === 'fulfilled') {
        const data = settingsResult.value.data || {};
        setSettings(data);
        setAccessType(getRestrictionMode(data) === 'none' ? 'public' : 'restricted');
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [me]);

  const restrictionMode = getRestrictionMode(settings);
  const publicForbidden = restrictionMode === 'always';
  const isSubsequent = reportType === 'subsequent';
  const isRestricted = accessType === 'restricted';

  const indiceCode = isSubsequent ? SUBSEQUENT_INDICE : (indiceValue ? indiceValue.padStart(2, '0') : '');
  const indiceColor = indiceCode ? getIndiceColor(indiceCode) : null;
  const indiceTone = indiceColor ? (isDark ? lighten(indiceColor, 0.35) : indiceColor) : c.text;

  const numberDigits = numberValue.replace(/\D/g, '');
  const normalizedNumber = normalizeReportNumber(numberValue);
  const normalizedYear = normalizeReportYear(yearValue);
  const yearNumber = parseInt(normalizedYear, 10);

  const serviceOptions = useMemo(
    () => [...new Set(users.map((person) => person.service).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr')),
    [users]
  );

  const errors = {
    indice: indiceCode ? '' : 'Requis',
    number: !numberDigits
      ? 'Indiquez le numéro du procès-verbal.'
      : (!normalizedNumber ? 'Numéro invalide (ex. 123 ou 000123).' : ''),
    year: !normalizedYear || yearNumber < 2000 || yearNumber > currentYear
      ? `L'année doit être comprise entre 2000 et ${currentYear}.`
      : '',
    unit: !unitValue.trim() ? 'Indiquez le service responsable.' : '',
    access: isRestricted && accessUsers.length === 0 ? 'Un document restreint doit être accessible à au moins une personne.' : ''
  };
  const firstError = FIELD_ORDER.find((field) => errors[field]);
  const shownError = (field) => (submitted ? errors[field] : '');

  const inputSx = getInputSx(c, isDark);
  const sectionTitleSx = { ...getSectionTitleSx(c, isDark), mb: 1.25 };

  const handleNumberChange = (event) => {
    const { number, year } = splitReportInput(event.target.value);
    if (number.length > 6) return;
    setNumberValue(number);

    // Un "/" tapé (ou collé, ex. "123/26") renvoie vers le champ année
    if (year !== null) {
      if (year) setYearValue(year);
      yearInputRef.current?.focus();
    }
  };

  // Complète avec les zéros (8 -> 08, 123 -> 000123) et l'année (26 -> 2026) en quittant le champ
  const handleIndiceBlur = () => {
    setIndiceValue((value) => (value.length === 1 ? value.padStart(2, '0') : value));
  };

  const handleNumberBlur = () => {
    setNumberValue((value) => normalizeReportNumber(value) ?? value);
  };

  const handleYearBlur = () => {
    setYearValue((value) => normalizeReportYear(value) ?? value);
  };

  const handleCancel = () => {
    pickReport(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (creating) return;
    setSubmitted(true);
    setSubmitError('');

    if (firstError) {
      const refs = { indice: indiceInputRef, number: numberInputRef, year: yearInputRef, unit: unitInputRef };
      refs[firstError]?.current?.focus();
      return;
    }

    setNumberValue(normalizedNumber);
    setYearValue(normalizedYear);
    setCreating(true);

    const formData = {
      reportType,
      indice: indiceCode,
      number: normalizedNumber,
      year: normalizedYear,
      unit: unitValue.trim(),
      access: {
        type: accessType,
        users: isRestricted ? accessUsers : []
      },
      createdBy: me,
      createdAt: new Date().toISOString(),
      sections: [],
      chat: []
    };

    try {
      const token = sessionStorage.getItem('authToken');
      const headers = { Authorization: `Bearer ${token}` };
      const { data: created } = await axios.post('/api/report', formData, { headers });

      logAction(LOG_ACTIONS.REPORT_CREATE, formatReportNumber(created.number, created.year), {
        type: isSubsequent ? 'subséquent' : 'initial',
        indice: formData.indice,
        unit: formData.unit,
        restricted: isRestricted,
        usersWithAccess: isRestricted ? accessUsers.length : 'tous'
      });

      try {
        await axios.post(
          '/api/lastreports/add',
          {
            indice: formData.indice,
            number: formData.number,
            year: formData.year,
            restricted: formData.access.type,
            createdBy: formData.createdBy,
            createdAt: formData.createdAt,
            reportType: formData.reportType,
            unit: formData.unit
          },
          { headers }
        );
      } catch (error) {
        console.error('Erreur lors de l\'ajout aux derniers documents :', error);
      }

      // Délai pour laisser le temps au serveur de traiter la création avant la navigation
      setTimeout(() => {
        pickReport({ number: created.number, year: created.year, new: false });
      }, 100);
    } catch (error) {
      setSubmitError(error.response?.data?.message || 'Erreur lors de la création du document.');
      setCreating(false);
    }
  };

  const errorAlertSx = isDark ? {
    backgroundColor: c.restrictedSoft,
    color: c.restricted,
    '& .MuiAlert-icon': { color: c.restricted }
  } : {};

  const renderSegment = (value, placeholder, color = c.text) => (
    <Box component="span" sx={{ color: value ? color : c.textSecondary, opacity: value ? 1 : 0.5 }}>
      {value || placeholder}
    </Box>
  );

  const renderSeparator = (char) => (
    <Box component="span" sx={{ color: c.textSecondary, mx: '0.06em' }}>{char}</Box>
  );

  const renderPreview = () => {
    const numberPadding = numberDigits && numberDigits.length < 6 ? '0'.repeat(6 - numberDigits.length) : '';

    return (
      <Typography
        component="div"
        aria-label="Numéro complet du procès-verbal"
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: { xs: 1.5, sm: 1.75 },
          mb: 3,
          borderRadius: '14px',
          backgroundColor: c.dialogBg,
          border: `1px solid ${c.border}`,
          borderLeft: `4px solid ${indiceColor || c.border}`,
          transition: 'border-color 0.2s ease',
          fontSize: { xs: '1.35rem', sm: '1.7rem' },
          fontWeight: 700,
          lineHeight: 1.3,
          letterSpacing: '0.02em',
          fontVariantNumeric: 'tabular-nums',
          color: c.text,
          wordBreak: 'break-all'
        }}
      >
        {renderSegment(settings?.shortJudicialDistrict, '··')}
        {renderSeparator('.')}
        {renderSegment(indiceCode, '··', indiceTone)}
        {renderSeparator('.')}
        {renderSegment(settings?.judicialDivision, '··')}
        {renderSeparator('.')}
        {numberDigits ? (
          <Box component="span">
            <Box component="span" sx={{ color: c.textSecondary, opacity: 0.5 }}>{numberPadding}</Box>
            {numberDigits}
          </Box>
        ) : renderSegment('', '······')}
        {renderSeparator('/')}
        {renderSegment(normalizedYear || yearValue, '····')}
      </Typography>
    );
  };

  return (
    <Container maxWidth="md" sx={{ pt: 4, pb: 6 }}>
      <Paper
        component="form"
        onSubmit={handleSubmit}
        noValidate
        autoComplete="off"
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: c.surface,
          backgroundImage: 'none',
          color: c.text,
          boxShadow: isDark ? '0 4px 20px rgba(0, 0, 0, 0.5)' : '0 4px 20px rgba(0, 0, 0, 0.05)'
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <NoteAddIcon aria-hidden="true" sx={{ fontSize: 38, color: 'primary.main', flexShrink: 0 }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'primary.main' }}>
              Nouveau document
            </Typography>
          </Box>

          <Divider sx={{ my: 3, borderColor: c.border }} />

          {renderPreview()}

          <Typography variant="body2" sx={sectionTitleSx}>
            Procès-verbal
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.25 }}>
            <ChoiceCard
              c={c}
              selected={!isSubsequent}
              onClick={() => setReportType('initial')}
              title="Initial"
              disabled={creating}
            />
            <ChoiceCard
              c={c}
              selected={isSubsequent}
              onClick={() => setReportType('subsequent')}
              title="Subséquent"
              disabled={creating}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isSubsequent ? 'minmax(0, 1fr) 120px' : '96px minmax(0, 1fr) 120px',
              alignItems: 'start',
              gap: 1.5,
              mt: 2.5
            }}
          >
            {!isSubsequent && (
              <TextField
                label="Indice"
                required
                value={indiceValue}
                onChange={(event) => setIndiceValue(event.target.value.replace(/\D/g, '').slice(0, 2))}
                onBlur={handleIndiceBlur}
                inputRef={indiceInputRef}
                disabled={creating}
                error={Boolean(shownError('indice'))}
                helperText={shownError('indice')}
                inputProps={{ inputMode: 'numeric', maxLength: 2, style: { fontVariantNumeric: 'tabular-nums' } }}
                sx={inputSx}
              />
            )}
            <TextField
              label="Numéro"
              required
              value={numberValue}
              onChange={handleNumberChange}
              onBlur={handleNumberBlur}
              inputRef={numberInputRef}
              placeholder="000123"
              disabled={creating}
              error={Boolean(shownError('number'))}
              helperText={shownError('number')}
              inputProps={{ inputMode: 'numeric', style: { fontVariantNumeric: 'tabular-nums' } }}
              sx={inputSx}
            />
            <TextField
              label="Année"
              required
              value={yearValue}
              onChange={(event) => setYearValue(event.target.value.replace(/\D/g, '').slice(0, 4))}
              onBlur={handleYearBlur}
              onFocus={(event) => event.target.select()}
              inputRef={yearInputRef}
              placeholder={String(currentYear)}
              disabled={creating}
              error={Boolean(shownError('year'))}
              helperText={shownError('year')}
              inputProps={{ inputMode: 'numeric', maxLength: 4, style: { fontVariantNumeric: 'tabular-nums' } }}
              sx={inputSx}
            />
          </Box>

          <Autocomplete
            freeSolo
            options={serviceOptions}
            inputValue={unitValue}
            onInputChange={(_, input) => setUnitValue(input)}
            disabled={creating}
            slotProps={getAutocompleteSlotProps(c)}
            sx={{ mt: 2 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Service responsable"
                required
                inputRef={unitInputRef}
                error={Boolean(shownError('unit'))}
                helperText={shownError('unit')}
                sx={inputSx}
              />
            )}
          />

          <Typography variant="body2" sx={{ ...sectionTitleSx, mt: 4 }}>
            Accès
          </Typography>
          <VisibilityChoice
            c={c}
            value={accessType}
            onChange={setAccessType}
            publicForbidden={publicForbidden}
            disabled={loading || creating}
            compact
          />

          {isRestricted && (
            <AuthorizedPeople
              c={c}
              isDark={isDark}
              users={users}
              loading={loading}
              value={accessUsers}
              onChange={setAccessUsers}
              me={me}
              lockSelf
              disabled={creating}
              showModeratorNote={false}
              sx={{ mt: 2.5 }}
            />
          )}

          {shownError('access') && (
            <Typography variant="caption" component="div" sx={{ color: c.restricted, mt: 1 }}>
              {errors.access}
            </Typography>
          )}

          {loadError && (
            <Alert severity="error" sx={{ mt: 2, ...errorAlertSx }}>
              {loadError}
            </Alert>
          )}
        </Box>

        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            px: { xs: 2, sm: 3 },
            py: 1.75,
            backgroundColor: c.surface,
            borderTop: `1px solid ${c.border}`,
            borderRadius: '0 0 12px 12px'
          }}
        >
          {submitError && (
            <Alert severity="error" onClose={() => setSubmitError('')} sx={{ flexBasis: '100%', ...errorAlertSx }}>
              {submitError}
            </Alert>
          )}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            {submitted && firstError && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: c.restricted }}>
                <WarningAmberIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Complétez les champs signalés.
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Button onClick={handleCancel} disabled={creating} sx={{ color: isDark ? c.cancelText : 'text.secondary' }}>
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={creating || loading}
              startIcon={creating ? <CircularProgress size={18} color="inherit" /> : <NoteAddIcon />}
              sx={{ borderRadius: 2, px: 3, fontWeight: 600, ...getPrimaryButtonSx(c, isDark) }}
            >
              {creating ? 'Création…' : 'Créer le document'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}

export default AddReport;
